import { createContext, useCallback, useContext, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { LoginModal } from '@/components/login/LoginModal'

interface LoginModalContextValue {
  openLogin: (onSuccess?: () => void) => void
}

const LoginModalContext = createContext<LoginModalContextValue | undefined>(
  undefined,
)

export const LoginModalProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false)
  const onSuccessRef = useRef<(() => void) | undefined>(undefined)

  const openLogin = useCallback((onSuccess?: () => void) => {
    onSuccessRef.current = onSuccess
    setIsOpen(true)
  }, [])

  const handleClose = () => setIsOpen(false)

  const handleConnect = () => {
    setIsOpen(false)
    onSuccessRef.current?.()
  }

  return (
    <LoginModalContext.Provider value={{ openLogin }}>
      {children}
      <LoginModal
        isOpen={isOpen}
        onClose={handleClose}
        onConnect={handleConnect}
      />
    </LoginModalContext.Provider>
  )
}

export const useLoginModal = () => {
  const context = useContext(LoginModalContext)

  if (context === undefined) {
    throw new Error('useLoginModal must be used within a LoginModalProvider')
  }

  return context
}
